import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, ArrowRight, ExternalLink, Loader2, Zap, Copy, Check, Linkedin, AtSign, Mail, AlertCircle, CheckCircle, ArrowDown, ArrowUp, RefreshCw, Rocket, Bot, Crosshair, TrendingUp, Shield, Flame, Code, Radio, Clock, Eye, BarChart3, X } from 'lucide-react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { GrowthAgentModal } from '../components/GrowthAgentModal';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const s = (v) => (v == null ? '' : typeof v === 'object' ? JSON.stringify(v) : String(v));

const BLOCKS = [
  { key: 'audience_block', label: 'Audience', opp: 'target_audience', w: 5 },
  { key: 'channel_block', label: 'Channel', opp: 'recommended_channel', w: 5 },
  { key: 'hook_block', label: 'Hook', opp: 'recommended_hook', w: 4 },
  { key: 'format_block', label: 'Format', opp: 'recommended_format', w: 3 },
  { key: 'objective_block', label: 'Goal', opp: 'primary_objective', w: 4 },
  { key: 'tone_block', label: 'Tone', opp: 'recommended_tone', w: 2 },
];

const REASONS = { audience_block: 'Audience shifted', channel_block: 'Channel mismatch', hook_block: 'Hook misaligned', format_block: 'Format less effective', objective_block: 'Goal changed', tone_block: 'Tone shifted' };

const MODE_MAP = {
  viral: { label: 'Viral', color: '#EF4444', icon: Flame },
  authority: { label: 'Authority', color: '#8B5CF6', icon: Shield },
  conversion: { label: 'Conversion', color: '#10B981', icon: TrendingUp },
  technical: { label: 'Technical', color: '#3B82F6', icon: Code },
  community: { label: 'Community', color: '#F59E0B', icon: Shield },
};

const PLATFORMS = [
  { key: 'linkedin_post', label: 'LinkedIn', Icon: Linkedin, color: '#0A66C2' },
  { key: 'x_thread', label: 'X', Icon: AtSign, color: '#1DA1F2' },
  { key: 'email_draft', label: 'Email', Icon: Mail, color: '#FF8A3D' },
];

function normalize(raw) {
  if (!raw) return {};
  if (Array.isArray(raw)) { const n = {}; ['audience_block','geography_block','channel_block','hook_block','format_block','objective_block','tone_block'].forEach((k,i) => { if (raw[i]) n[k] = raw[i]; }); return n; }
  return raw;
}

export default function DashboardPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [project, setProject] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [blocks, setBlocks] = useState({});
  const [origBlocks, setOrigBlocks] = useState({});
  const [activeAlt, setActiveAlt] = useState(-1);
  const [genContent, setGenContent] = useState({});
  const [genType, setGenType] = useState(null);
  const [copied, setCopied] = useState(false);
  const [platform, setPlatform] = useState('linkedin_post');
  const [variation, setVariation] = useState('safe');
  const [liveTitle, setLiveTitle] = useState(null);
  const [liveHeadline, setLiveHeadline] = useState(null);
  const [liveScore, setLiveScore] = useState(null);
  const [regen, setRegen] = useState(false);
  const [showAgent, setShowAgent] = useState(false);
  const [lastDeploy, setLastDeploy] = useState(null);
  const [edgeSignal, setEdgeSignal] = useState(null);
  const [animScore, setAnimScore] = useState(0);
  const [signalStats, setSignalStats] = useState(null);
  const [showReplay, setShowReplay] = useState(false);
  const [replayStep, setReplayStep] = useState(0);
  const [expiresIn, setExpiresIn] = useState('');
  const [perfHistory, setPerfHistory] = useState([]);
  const [learning, setLearning] = useState(null);
  const [collective, setCollective] = useState(null);
  const [daily, setDaily] = useState(null);
  const [history, setHistory] = useState(null);
  const [mobileTab, setMobileTab] = useState('strategy');
  const timer = useRef(null);

  const fetchProject = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/projects/${id}`);
      setProject(r.data);
      const b = normalize(r.data.analysis?.strategy_blocks);
      setBlocks(b); setOrigBlocks(b);
      try { const dRes = await axios.get(`${API}/projects/${id}/deployments`); if (dRes.data?.length) setLastDeploy(dRes.data[0]); } catch {}
    } catch { setError('Failed to load'); } finally { setLoading(false); }
  }, [id]);

  // Fetch edge signal + stats
  const fetchEdge = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/market-signals`);
      if (r.data?.signals?.length) {
        const sig = r.data.signals[Math.floor(Math.random() * Math.min(r.data.signals.length, 3))];
        setEdgeSignal(sig);
      }
      if (r.data?.stats) setSignalStats(r.data.stats);
      // Compute expiry from generated_at
      if (r.data?.generated_at) {
        const gen = new Date(r.data.generated_at);
        const expires = new Date(gen.getTime() + 4 * 60 * 60 * 1000); // 4hr cache
        const diffMs = expires - new Date();
        if (diffMs > 0) {
          const h = Math.floor(diffMs / 3600000);
          const m = Math.floor((diffMs % 3600000) / 60000);
          setExpiresIn(`${h}h ${m}m`);
        } else {
          setExpiresIn('Refreshing soon');
        }
      }
    } catch {}
  }, []);

  useEffect(() => { fetchProject(); fetchEdge(); }, [fetchProject, fetchEdge]);

  // Fetch performance + learning + collective + daily + history
  useEffect(() => {
    if (!id) return;
    const loadPerf = async () => {
      try {
        const [perfRes, learnRes, collectiveRes, dailyRes, histRes] = await Promise.all([
          axios.get(`${API}/projects/${id}/performance`),
          axios.get(`${API}/projects/${id}/learning`),
          axios.get(`${API}/collective-intelligence`).catch(() => ({ data: null })),
          axios.get(`${API}/projects/${id}/daily`).catch(() => ({ data: null })),
          axios.get(`${API}/projects/${id}/history`).catch(() => ({ data: null }))
        ]);
        setPerfHistory(perfRes.data || []);
        if (learnRes.data && Object.keys(learnRes.data).length > 0) setLearning(learnRes.data);
        if (collectiveRes.data?.intelligence) setCollective(collectiveRes.data);
        if (dailyRes.data) setDaily(dailyRes.data);
        if (histRes.data) setHistory(histRes.data);
      } catch {}
    };
    loadPerf();
  }, [id]);

  const projectStatus = project?.status;
  useEffect(() => {
    if (!projectStatus || projectStatus === 'complete' || projectStatus === 'error') return;
    const iv = setInterval(async () => { try { const r = await axios.get(`${API}/projects/${id}`); if (r.data.status === 'complete') { setProject(r.data); const b = normalize(r.data.analysis?.strategy_blocks); setBlocks(b); setOrigBlocks(b); clearInterval(iv); } } catch {} }, 3000);
    return () => clearInterval(iv);
  }, [projectStatus, id]);

  useEffect(() => {
    const h = (e) => { if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA') return; if (e.key === 'Escape') navigate('/'); };
    window.addEventListener('keydown', h); return () => window.removeEventListener('keydown', h);
  }, [navigate]);

  const a = project?.analysis || {};
  const fr = project?.fast_result || {};
  const opp = useMemo(() => activeAlt === -1 ? (a.best_marketing_opportunity_today || {})
    : (() => { const alt = a.alternative_opportunities?.[activeAlt]; return alt ? { title: alt.title, target_audience: alt.audience, recommended_channel: alt.channel, recommended_hook: alt.hook, recommended_format: alt.format, primary_objective: alt.objective, confidence_score: alt.confidence_score, opportunity_summary: alt.why_it_could_work, recommended_tone: a.best_marketing_opportunity_today?.recommended_tone } : {}; })(), [activeAlt, a.best_marketing_opportunity_today, a.alternative_opportunities]);

  const bv = (key) => { const b = blocks[key]; if (b?.selected_value) return b.selected_value; const f = BLOCKS.find(x => x.key === key); return f ? (opp[f.opp] || '') : ''; };
  const bo = (key) => { const b = blocks[key]; const c = bv(key); return [c, ...(b?.alternatives || [])].filter((v,i,ar) => v && ar.indexOf(v) === i); };

  useEffect(() => {
    const upd = { ...blocks }; let changed = false;
    BLOCKS.forEach(({ key, opp: of2 }) => { if (!upd[key]?.selected_value && opp[of2]) { upd[key] = { ...(upd[key] || {}), selected_value: opp[of2] }; changed = true; } });
    if (changed && !blocks._init) { setBlocks({ ...upd, _init: true }); if (!origBlocks._init) setOrigBlocks({ ...upd, _init: true }); }
  }, [opp, blocks, origBlocks]);

  const title = liveTitle || s(opp.title);
  const rawScore = opp.confidence_score || 0;
  const normalizedDeep = rawScore > 0 && rawScore <= 10 ? rawScore * 10 : rawScore;
  const score = liveScore || normalizedDeep || fr.confidence || 0;
  const bestMode = fr.best_mode || 'authority';
  const modeInfo = MODE_MAP[bestMode] || MODE_MAP.authority;
  const ModeIcon = modeInfo.icon;

  // Animate score
  useEffect(() => {
    if (!score) return;
    let start = 0; const end = score; const dur = 800; const t0 = Date.now();
    const tick = () => { const p = Math.min((Date.now() - t0) / dur, 1); setAnimScore(Math.round(start + (end - start) * p)); if (p < 1) requestAnimationFrame(tick); };
    tick();
  }, [score]);

  const { delta, modKeys, reason } = (() => {
    let d = 0; const mk = [];
    BLOCKS.forEach(({ key, w }) => { if (blocks[key]?.selected_value && origBlocks[key]?.selected_value && blocks[key].selected_value !== origBlocks[key].selected_value) { d -= w; mk.push(key); } });
    return { delta: d, modKeys: mk, reason: mk.length ? (REASONS[mk[0]] || 'Strategy modified') : '' };
  })();

  const onChange = (key, val) => {
    const upd = { ...blocks, [key]: { ...blocks[key], selected_value: val } };
    setBlocks(upd);
    axios.put(`${API}/projects/${id}/blocks`, { strategy_blocks: upd }).catch(() => {});
    if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(async () => {
      setRegen(true);
      try { const r = await axios.post(`${API}/projects/${id}/regenerate-move`, { strategy_blocks: upd }); setLiveTitle(r.data.title); setLiveHeadline(r.data.headline); setLiveScore(r.data.confidence); } catch {} finally { setRegen(false); }
    }, 800);
  };

  const contentKey = `${platform}_${variation}`;

  const generate = async (type, vari) => {
    const k = `${type}_${vari || variation}`;
    setGenType(type); setPlatform(type);
    try { const r = await axios.post(`${API}/projects/${id}/generate-content`, { content_type: type, opportunity: opp, variation: vari || variation }, { timeout: 120000 }); setGenContent(p => ({ ...p, [k]: r.data.content })); } catch {} finally { setGenType(null); }
  };

  const copy = (t) => {
    const footer = `\n\n—\nGenerated by NextMove | ${modeInfo.label} Strategy | Score: ${score}/100 | Based on ${signalStats?.total_analyzed || '—'} signals`;
    try { navigator.clipboard.writeText(t + footer).catch(() => {}); } catch {}
    setCopied(true); setTimeout(() => setCopied(false), 1500);
  };
  const content = genContent[contentKey] || (platform === 'linkedin_post' && variation === 'safe' ? opp.suggested_post : null);

  // Format content for readability
  const formatContent = (text) => {
    if (!text) return null;
    return text.split('\n').filter(l => l.trim()).map((line, i) => (
      <p key={i} className="mb-2 last:mb-0" style={{ lineHeight: '1.7' }}>
        {line.trim()}
      </p>
    ));
  };

  // Mode scores for impact preview
  const modeScores = fr.mode_scores || {};

  if (loading) return <div className="h-screen bg-[#07070A] flex items-center justify-center"><Loader2 className="w-6 h-6 text-[#7C5CFF] animate-spin" /></div>;
  if (error || !project) return <div className="h-screen bg-[#07070A] flex items-center justify-center flex-col gap-3"><p className="text-white text-base">{error || 'Not found'}</p><button onClick={() => navigate('/')} className="text-[#7C5CFF] text-sm">Back home</button></div>;

  return (
    <div className="h-screen md:overflow-hidden overflow-y-auto bg-[#07070A] flex flex-col font-landing" data-testid="dashboard-page">
      {/* HEADER */}
      <header className="h-11 flex items-center px-3 md:px-5 border-b flex-shrink-0 gap-2 md:gap-3" style={{ borderColor: 'rgba(255,255,255,0.06)', background: '#0A0A0F' }}>
        <button data-testid="back-btn" onClick={() => navigate('/')} className="text-zinc-400 hover:text-white transition-colors"><ArrowLeft className="w-4 h-4" /></button>
        <h1 className="text-sm font-semibold text-white truncate">{a.project_overview?.project_name || 'Analysis'}</h1>
        <a href={project.project_url} target="_blank" rel="noopener noreferrer" className="text-zinc-400 hover:text-[#7C5CFF] text-xs items-center gap-1 truncate max-w-[100px] md:max-w-[140px] hidden sm:flex">{project.project_url?.replace(/https?:\/\//, '').split('/')[0]}<ExternalLink className="w-3 h-3" /></a>
        <div className="flex-1" />
        <div className="flex items-center gap-1.5 px-2 py-1 rounded-lg" style={{ background: `${modeInfo.color}10`, border: `1px solid ${modeInfo.color}20` }}>
          <ModeIcon className="w-3 h-3" style={{ color: modeInfo.color }} />
          <span className="text-[10px] font-mono font-bold uppercase" style={{ color: modeInfo.color }}>{modeInfo.label}</span>
        </div>
        <span className="text-[9px] font-mono uppercase tracking-wider px-2 py-0.5 rounded-md text-zinc-400 hidden md:inline" style={{ background: '#0F0F14' }}>{s(a.project_overview?.stage_guess)}</span>
      </header>

      {/* Mobile Tab Bar */}
      <div className="flex md:hidden border-b flex-shrink-0" style={{ borderColor: 'rgba(255,255,255,0.06)' }}>
        {[
          { key: 'strategy', label: 'Strategy' },
          { key: 'dna', label: 'DNA' },
          { key: 'controls', label: 'Controls' },
        ].map(({ key, label }) => (
          <button key={key} onClick={() => setMobileTab(key)} data-testid={`mobile-tab-${key}`}
            className={`flex-1 py-2 text-[11px] font-mono uppercase tracking-wider transition-all ${mobileTab === key ? 'text-[#7C5CFF] border-b-2 border-[#7C5CFF]' : 'text-zinc-400'}`}>
            {label}
          </button>
        ))}
      </div>

      {/* PANELS — flex on desktop, stacked on mobile */}
      <div className="flex flex-1 overflow-hidden">

        {/* ═══ LEFT — Product DNA ═══ */}
        <div className={`${mobileTab === 'dna' ? 'flex' : 'hidden'} md:flex w-full md:w-[22%] border-r p-4 flex-col flex-shrink-0 overflow-y-auto relative`} style={{ borderColor: 'rgba(255,255,255,0.06)', scrollbarWidth: 'thin', scrollbarColor: '#1F1F28 transparent' }}>
          <h3 className="text-[10px] font-mono uppercase tracking-[0.15em] text-zinc-400 mb-3">Product DNA</h3>
          <div className="space-y-3 text-[12px]">
            <div><p className="text-zinc-400 text-[9px] font-mono uppercase mb-0.5">What it is</p><p className="text-zinc-200 leading-relaxed">{s(fr.product_truth || a.project_overview?.one_line_summary)}</p></div>
            <div><p className="text-zinc-400 text-[9px] font-mono uppercase mb-0.5">Who it's for</p><p className="text-zinc-200 leading-relaxed">{s(fr.core_user || opp.target_audience)}</p></div>
            {a.project_overview?.category && <div><p className="text-zinc-400 text-[9px] font-mono uppercase mb-0.5">Category</p><p className="text-[#7C5CFF] text-[11px] font-medium">{s(a.project_overview.category)}</p></div>}
            {(a.project_overview?.observed_strengths || [])[0] && <div><p className="text-zinc-400 text-[9px] font-mono uppercase mb-0.5 flex items-center gap-1"><CheckCircle className="w-3 h-3 text-[#22C55E]" />Edge</p><p className="text-zinc-300 leading-relaxed">{s(a.project_overview.observed_strengths[0])}</p></div>}
            {(a.project_overview?.observed_weaknesses || [])[0] && <div><p className="text-zinc-400 text-[9px] font-mono uppercase mb-0.5 flex items-center gap-1"><AlertCircle className="w-3 h-3 text-[#F59E0B]" />Risk</p><p className="text-zinc-400 leading-relaxed">{s(a.project_overview.observed_weaknesses[0])}</p></div>}
            {a.brand_tone?.tone_style && <div><p className="text-zinc-400 text-[9px] font-mono uppercase mb-0.5">Tone</p><p className="text-zinc-400 text-[11px]">{s(a.brand_tone.tone_style)}</p></div>}
          </div>

          {/* Top Channels */}
          {(a.channels || []).length > 0 && (
            <div className="pt-3 border-t space-y-1" style={{ borderColor: 'rgba(255,255,255,0.06)' }}>
              <p className="text-zinc-400 text-[9px] font-mono uppercase">Top Channels</p>
              {(a.channels || []).slice(0, 2).map((ch, i) => (
                <div key={i} className="rounded-lg px-2 py-1.5" style={{ background: '#0F0F14' }}>
                  <p className="text-[11px] text-zinc-200 font-medium">{s(ch.channel_name)}</p>
                  <p className="text-[9px] text-zinc-400 leading-snug">{s(ch.why_it_fits).slice(0, 80)}</p>
                </div>
              ))}
            </div>
          )}

          {/* Top Audiences */}
          {(a.audiences || []).length > 0 && (
            <div className="pt-3 border-t space-y-1" style={{ borderColor: 'rgba(255,255,255,0.06)' }}>
              <p className="text-zinc-400 text-[9px] font-mono uppercase">Audiences</p>
              {(a.audiences || []).slice(0, 2).map((au, i) => (
                <div key={i} className="rounded-lg px-2 py-1.5" style={{ background: '#0F0F14' }}>
                  <p className="text-[11px] text-zinc-200 font-medium">{s(au.audience_name)}</p>
                  <p className="text-[9px] text-zinc-400">{s(au.why_it_fits).slice(0, 60)}</p>
                </div>
              ))}
            </div>
          )}

          {/* Performance History */}
          {perfHistory.length > 0 ? (
            <div className="pt-3 border-t space-y-1.5" style={{ borderColor: 'rgba(255,255,255,0.06)' }}>
              <p className="text-zinc-400 text-[9px] font-mono uppercase flex items-center gap-1"><BarChart3 className="w-3 h-3" />Your Performance</p>
              {perfHistory.slice(0, 3).map((dep, i) => {
                const rating = dep.feedback?.rating;
                const icon = rating === 'good' ? '↑' : rating === 'poor' ? '↓' : '→';
                const color = rating === 'good' ? '#22C55E' : rating === 'poor' ? '#EF4444' : '#F59E0B';
                const plat = dep.platform?.replace('_', ' ');
                return (
                  <div key={dep.id || i} className="flex items-center gap-1.5 px-2 py-1 rounded-lg" style={{ background: '#0F0F14' }} data-testid={`perf-${i}`}>
                    <span className="text-sm font-bold font-mono" style={{ color }}>{icon}</span>
                    <span className="text-[10px] text-zinc-300 truncate flex-1 capitalize">{plat}</span>
                    {dep.payload?.score && <span className="text-[9px] font-mono text-zinc-400">{dep.payload.score}</span>}
                    {rating && <span className="text-[8px] font-mono px-1 py-0.5 rounded" style={{ color, background: `${color}10` }}>{rating}</span>}
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="pt-3 border-t" style={{ borderColor: 'rgba(255,255,255,0.06)' }}>
              <p className="text-zinc-400 text-[9px] font-mono uppercase">Performance</p>
              <p className="text-[10px] text-zinc-400 mt-0.5">Deploy a strategy to start tracking</p>
            </div>
          )}}

          {/* Learning Engine */}
          {learning?.learnings?.length > 0 && (
            <div className="pt-3 border-t space-y-1.5" style={{ borderColor: 'rgba(255,255,255,0.06)' }}>
              <p className="text-zinc-400 text-[9px] font-mono uppercase flex items-center gap-1"><RefreshCw className="w-3 h-3 text-[#7C5CFF]" />Learning Engine</p>
              <p className="text-[10px] text-zinc-400 italic">{learning.performance_summary}</p>
              {learning.learnings.slice(0, 2).map((l, i) => (
                <p key={i} className="text-[10px] text-zinc-400 flex items-start gap-1"><ArrowRight className="w-3 h-3 text-[#7C5CFF] flex-shrink-0 mt-0.5" />{l}</p>
              ))}
              {learning.next_edge?.headline && (
                <div className="rounded-lg px-2 py-1.5 mt-1" style={{ background: 'rgba(124,92,255,0.06)', border: '1px solid rgba(124,92,255,0.1)' }}>
                  <p className="text-[9px] font-mono text-[#7C5CFF] uppercase">Applying to today</p>
                  <p className="text-[10px] text-zinc-300 leading-snug">{learning.next_edge.headline}</p>
                </div>
              )}
            </div>
          )}

          {/* Mode scores mini-bar */}
          {Object.keys(modeScores).length > 0 && (
            <div className="pt-3 border-t space-y-1.5" style={{ borderColor: 'rgba(255,255,255,0.06)' }}>
              <p className="text-zinc-400 text-[9px] font-mono uppercase">Modes</p>
              {Object.entries(modeScores).map(([mode, sc]) => {
                const m = MODE_MAP[mode]; if (!m) return null;
                return (
                  <div key={mode} className="flex items-center gap-1.5">
                    <span className="text-[9px] font-mono w-14 text-zinc-400 capitalize">{mode}</span>
                    <div className="flex-1 h-1 rounded-full" style={{ background: '#15151D' }}>
                      <div className="h-full rounded-full transition-all duration-700" style={{ width: `${sc}%`, background: mode === bestMode ? m.color : '#2A2A35' }} />
                    </div>
                    <span className="text-[9px] font-mono w-6 text-right" style={{ color: mode === bestMode ? m.color : '#52525B' }}>{sc}</span>
                  </div>
                );
              })}
            </div>
          )}

          {/* Growth History */}
          {history && history.total > 0 && (
            <div className="pt-3 border-t space-y-1.5" style={{ borderColor: 'rgba(255,255,255,0.06)' }} data-testid="growth-history">
              <p className="text-zinc-400 text-[9px] font-mono uppercase flex items-center gap-1"><TrendingUp className="w-3 h-3" />Growth History</p>
              <div className="grid grid-cols-2 gap-1.5">
                <div className="rounded-lg px-2 py-1.5 text-center" style={{ background: '#0F0F14' }}>
                  <p className="text-sm font-mono font-bold text-[#22C55E]">{history.summary.win_rate}%</p>
                  <p className="text-[7px] font-mono text-zinc-400 uppercase">Win rate</p>
                </div>
                <div className="rounded-lg px-2 py-1.5 text-center" style={{ background: '#0F0F14' }}>
                  <p className="text-sm font-mono font-bold text-white">{history.total}</p>
                  <p className="text-[7px] font-mono text-zinc-400 uppercase">Deployed</p>
                </div>
              </div>
              {history.best_patterns?.platform?.name && (
                <div className="rounded-lg px-2 py-1 text-center" style={{ background: 'rgba(124,92,255,0.04)', border: '1px solid rgba(124,92,255,0.08)' }}>
                  <p className="text-[8px] font-mono text-zinc-400 uppercase">Best pattern</p>
                  <p className="text-[10px] text-zinc-300">{history.best_patterns.mode?.name || '?'} on {history.best_patterns.platform?.name?.replace('_', ' ') || '?'}</p>
                </div>
              )}
              {/* Score timeline mini-chart */}
              {history.score_timeline?.length > 1 && (
                <div className="flex items-end gap-px h-6">
                  {history.score_timeline.slice(-12).map((pt, i) => (
                    <div key={i} className="flex-1 rounded-t" style={{
                      height: `${Math.max(10, (pt.score / 100) * 100)}%`,
                      background: pt.rating === 'good' ? '#22C55E' : pt.rating === 'poor' ? '#EF4444' : '#3F3F46',
                      opacity: 0.7
                    }} title={`${pt.score} - ${pt.rating}`} />
                  ))}
                </div>
              )}
            </div>
          )}
        </div>

        {/* ═══ CENTER — Action Panel ═══ */}
        <div className={`${mobileTab === 'strategy' ? 'flex' : 'hidden'} md:flex w-full md:w-[58%] flex-col overflow-hidden`}>

          {/* Live Edge Feed — Network Intelligence */}
          <div className="flex items-center gap-2 px-5 py-1.5 flex-shrink-0" style={{ background: 'linear-gradient(90deg, rgba(124,92,255,0.06), rgba(255,138,61,0.04))', borderBottom: '1px solid rgba(124,92,255,0.1)' }} data-testid="edge-signal">
            <Radio className="w-3 h-3 text-[#22C55E] animate-pulse" />
            <span className="text-[10px] font-mono font-bold text-[#FF8A3D] uppercase">Live Edge</span>
            {(collective?.intelligence?.edge_feed || (edgeSignal ? [edgeSignal] : [])).slice(0, 3).map((ef, i) => (
              <span key={i} className="flex items-center gap-1 text-[10px]">
                <span className="text-zinc-400 font-mono">{ef.industry || ef.category}</span>
                <span className="font-mono font-bold" style={{ color: (ef.direction || 'up') === 'up' ? '#22C55E' : '#EF4444' }}>{ef.delta}</span>
                {i < 2 && <span className="text-zinc-400">·</span>}
              </span>
            ))}
            <div className="flex-1" />
            {expiresIn && <span className="text-[9px] font-mono text-zinc-400 flex items-center gap-1 flex-shrink-0"><Clock className="w-3 h-3" />{expiresIn}</span>}
          </div>

          {/* Scrollable center content */}
          <div className="flex-1 overflow-y-auto px-5 py-4 space-y-4" style={{ scrollbarWidth: 'thin', scrollbarColor: '#1F1F28 transparent' }}>

            {/* ═══ DAILY RITUAL ═══ */}
            {daily && (daily.streak > 0 || daily.total_deployments > 0) && (
              <div className="flex items-center gap-3" data-testid="daily-ritual">
                {/* Streak */}
                {daily.streak > 0 && (
                  <div className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl" style={{ background: 'rgba(255,138,61,0.06)', border: '1px solid rgba(255,138,61,0.15)' }}>
                    <span className="text-sm">🔥</span>
                    <span className="text-[11px] font-mono font-bold text-[#FF8A3D]">{daily.streak}-day streak</span>
                  </div>
                )}
                {/* Yesterday vs Today */}
                <div className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl" style={{ background: '#0F0F14', border: '1px solid rgba(255,255,255,0.04)' }}>
                  <span className="text-[10px] text-zinc-400">Yesterday:</span>
                  <span className={`text-[10px] font-mono font-bold ${daily.yesterday_performance === 'good' ? 'text-[#22C55E]' : daily.yesterday_performance === 'poor' ? 'text-[#EF4444]' : 'text-zinc-400'}`}>
                    {daily.yesterday_performance === 'good' ? '↑ Good' : daily.yesterday_performance === 'poor' ? '↓ Poor' : daily.yesterday_performance === 'average' ? '→ Avg' : '—'}
                  </span>
                  <span className="text-zinc-400">→</span>
                  <span className="text-[10px] font-mono text-[#7C5CFF]">Today: {daily.yesterday_vs_today?.adjustment}</span>
                </div>
                {/* Win rate */}
                {daily.win_rate > 0 && (
                  <div className="flex items-center gap-1 px-2 py-1 rounded-lg" style={{ background: 'rgba(34,197,94,0.06)', border: '1px solid rgba(34,197,94,0.12)' }}>
                    <span className="text-[10px] font-mono font-bold text-[#22C55E]">{daily.win_rate}%</span>
                    <span className="text-[9px] text-zinc-400">win</span>
                  </div>
                )}
              </div>
            )}

            {/* Today's Move Header */}
            <div className="flex items-center gap-4">
              <div className="flex-1 min-w-0">
                <p className="text-[9px] font-mono uppercase tracking-[0.15em] text-zinc-400 mb-1">Today's Move</p>
                <h2 className="text-xl font-bold text-white leading-tight" data-testid="opportunity-title">{title || 'Loading...'}</h2>
                {/* Intelligence credibility */}
                <div className="flex items-center gap-2 mt-1.5">
                  {signalStats && <span className="text-[9px] font-mono text-zinc-400">Generated from {signalStats.total_analyzed?.toLocaleString() || '—'} signals</span>}
                  {project?.updated_at && <span className="text-[9px] font-mono text-zinc-400">Updated {(() => { const d = Math.floor((Date.now() - new Date(project.updated_at).getTime()) / 60000); return d < 1 ? 'just now' : d < 60 ? `${d}m ago` : `${Math.floor(d/60)}h ago`; })()}</span>}
                  <button onClick={() => { setShowReplay(true); setReplayStep(0); }} data-testid="replay-btn" className="text-[9px] font-mono text-[#7C5CFF] hover:text-white transition-colors flex items-center gap-0.5 ml-1"><Eye className="w-3 h-3" />Replay</button>
                </div>
              </div>
              <div className="flex-shrink-0 w-16 h-16 rounded-2xl flex flex-col items-center justify-center relative" style={{ background: `${modeInfo.color}0A`, border: `1px solid ${modeInfo.color}20`, boxShadow: `0 0 20px ${modeInfo.color}10` }}>
                <span className="font-mono text-xl font-bold" style={{ color: animScore >= 70 ? '#22C55E' : animScore >= 40 ? '#F59E0B' : '#EF4444' }} data-testid="confidence-score">{animScore}</span>
                <span className="text-[7px] font-mono text-zinc-400 uppercase">score</span>
                {regen && <RefreshCw className="w-3 h-3 text-[#7C5CFF] animate-spin absolute -top-1 -right-1" />}
              </div>
            </div>

            {/* ═══ DO THIS NOW — Primary Card ═══ */}
            <div className="rounded-2xl relative overflow-hidden" data-testid="do-this-now-module"
              style={{ background: '#0E0E14', border: '1px solid rgba(124,92,255,0.12)', boxShadow: '0 0 40px rgba(124,92,255,0.06), 0 0 80px rgba(255,138,61,0.03)' }}>
              <div className="absolute top-0 left-0 right-0 h-[2px]" style={{ background: 'linear-gradient(90deg, #7C5CFF, #FF8A3D, #7C5CFF)', opacity: 0.6 }} />

              {/* Header: DO THIS NOW + Platform Toggles */}
              <div className="flex items-center justify-between px-4 py-2.5 border-b" style={{ borderColor: 'rgba(255,255,255,0.06)' }}>
                <div className="flex items-center gap-2">
                  <Zap className="w-3.5 h-3.5 text-[#FF8A3D]" />
                  <h3 className="text-[11px] font-semibold uppercase tracking-wider text-white">Do This Now</h3>
                </div>
                <div className="flex gap-1">
                  {PLATFORMS.map(({ key, label, Icon, color }) => (
                    <button key={key} data-testid={`exec-${key}`}
                      onClick={() => { setPlatform(key); if (!genContent[`${key}_${variation}`]) generate(key, variation); }}
                      className={`flex items-center gap-1 px-2.5 py-1 rounded-lg text-[10px] font-medium transition-all ${platform === key ? 'text-white' : 'text-zinc-400 hover:text-zinc-400'}`}
                      style={platform === key ? { background: `${color}15`, border: `1px solid ${color}30`, boxShadow: `0 0 12px ${color}20` } : { border: '1px solid transparent' }}>
                      <Icon className="w-3 h-3" />{label}{genType === key && <Loader2 className="w-3 h-3 animate-spin" />}
                    </button>
                  ))}
                </div>
              </div>

              {/* Variation toggle */}
              <div className="flex items-center gap-1 px-4 py-1.5 border-b" style={{ borderColor: 'rgba(255,255,255,0.04)' }} data-testid="variation-toggle">
                {[{ key: 'safe', label: 'Safe', c: '#22C55E' }, { key: 'bold', label: 'Bold', c: '#EF4444' }, { key: 'direct', label: 'Direct', c: '#3B82F6' }].map(({ key, label, c }) => (
                  <button key={key} data-testid={`variation-${key}`}
                    onClick={() => { setVariation(key); if (!genContent[`${platform}_${key}`]) generate(platform, key); }}
                    className={`px-2.5 py-1 rounded-md text-[10px] font-medium transition-all ${variation === key ? 'text-white' : 'text-zinc-400 hover:text-zinc-400'}`}
                    style={variation === key ? { background: `${c}12`, border: `1px solid ${c}25` } : { border: '1px solid transparent' }}>
                    {label}
                  </button>
                ))}
              </div>

              {/* Section 1: Post Content */}
              <div className="px-4 py-3">
                {genType === platform ? (
                  <div className="flex items-center justify-center gap-2 py-6 text-zinc-400 text-sm"><Loader2 className="w-4 h-4 animate-spin" />Generating...</div>
                ) : content ? (
                  <div>
                    <p className="text-[9px] font-mono uppercase tracking-wider text-zinc-400 mb-2">Post this:</p>
                    <div className="text-[13px] text-zinc-200" data-testid="generated-content" style={{ lineHeight: '1.75' }}>
                      {formatContent(content)}
                    </div>
                  </div>
                ) : (
                  <div className="flex flex-col items-center py-6 gap-2">
                    <p className="text-xs text-zinc-400">Select a platform to generate</p>
                    <button onClick={() => generate(platform, variation)} data-testid="generate-btn"
                      className="text-sm font-medium flex items-center gap-1.5 px-5 py-2 rounded-xl text-white btn-glow"
                      style={{ background: 'linear-gradient(135deg, #FF8A3D, #FF7A18)', boxShadow: '0 4px 16px rgba(255,138,61,0.3)' }}>
                      <Zap className="w-4 h-4" />Generate
                    </button>
                  </div>
                )}
              </div>

              {/* Section 2: Why This Works + Expected Outcome with metrics */}
              {content && (
                <div className="px-4 pb-3 grid grid-cols-2 gap-3">
                  <div className="rounded-xl px-3 py-2.5" style={{ background: '#15151D', border: '1px solid rgba(255,255,255,0.04)' }}>
                    <p className="text-[9px] font-mono uppercase tracking-wider text-[#22C55E] mb-1.5 flex items-center gap-1"><CheckCircle className="w-3 h-3" />Why this works</p>
                    <p className="text-[11px] text-zinc-300 leading-relaxed">{s(opp.why_this_is_the_best_today) || `${modeInfo.label} content is trending in your category`}</p>
                    {edgeSignal && <p className="text-[10px] text-[#22C55E] mt-1 font-mono">{edgeSignal.category} {edgeSignal.delta}</p>}
                  </div>
                  <div className="rounded-xl px-3 py-2.5" style={{ background: '#15151D', border: '1px solid rgba(255,255,255,0.04)' }}>
                    <p className="text-[9px] font-mono uppercase tracking-wider text-[#7C5CFF] mb-1.5 flex items-center gap-1"><BarChart3 className="w-3 h-3" />Expected Impact</p>
                    <div className="grid grid-cols-3 gap-2">
                      <div className="text-center">
                        <p className="text-sm font-mono font-bold text-[#22C55E]">+{Math.min(Math.round(score * 0.35), 42)}%</p>
                        <p className="text-[8px] font-mono text-zinc-400">Engagement</p>
                      </div>
                      <div className="text-center">
                        <p className="text-sm font-mono font-bold text-[#3B82F6]">+{Math.min(Math.round(score * 0.2), 24)}%</p>
                        <p className="text-[8px] font-mono text-zinc-400">Conversion</p>
                      </div>
                      <div className="text-center">
                        <p className="text-sm font-mono font-bold text-[#8B5CF6]">{(score * 0.025).toFixed(1)}x</p>
                        <p className="text-[8px] font-mono text-zinc-400">{modeInfo.label}</p>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Deploy Bar — Execution Confidence */}
              <div className="px-4 py-2.5 border-t flex-shrink-0" style={{ borderColor: 'rgba(255,255,255,0.06)' }}>
                <div className="flex items-center gap-2">
                  <button data-testid="copy-content-btn" onClick={() => content && copy(content)} disabled={!content}
                    className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[11px] font-medium transition-all disabled:opacity-30 text-zinc-300 hover:text-white hover:bg-[#1A1A22]" style={{ background: '#15151D' }}>
                    {copied ? <Check className="w-3.5 h-3.5 text-[#22C55E]" /> : <Copy className="w-3.5 h-3.5" />}{copied ? 'Copied!' : 'Copy'}
                  </button>
                  <button data-testid="regenerate-btn" onClick={() => generate(platform, variation)} disabled={genType === platform}
                    className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[11px] font-medium text-zinc-400 hover:text-zinc-200 hover:bg-[#1A1A22] transition-all disabled:opacity-30" style={{ background: '#15151D' }}>
                    <RefreshCw className={`w-3.5 h-3.5 ${genType === platform ? 'animate-spin' : ''}`} />Regenerate
                  </button>
                  <div className="flex-1" />
                  <div className="flex flex-col items-end gap-0.5">
                    <button data-testid="deploy-agent-btn" onClick={() => setShowAgent(true)}
                      className="flex items-center gap-1.5 px-5 py-2 rounded-xl text-[11px] font-semibold text-white transition-all hover:-translate-y-0.5 btn-glow"
                      style={{ background: 'linear-gradient(135deg, #7C5CFF, #FF8A3D)', boxShadow: '0 4px 20px rgba(124,92,255,0.25), 0 2px 10px rgba(255,138,61,0.15)' }}>
                      <Rocket className="w-3.5 h-3.5" />Deploy Strategy
                    </button>
                    <p className="text-[8px] font-mono text-zinc-400 text-right">Send to {PLATFORMS.find(p => p.key === platform)?.label} + track + auto-update</p>
                  </div>
                </div>
              </div>
            </div>

            {/* ═══ STRATEGY VARIATIONS — 3 Alternative Cards ═══ */}
            {(a.alternative_opportunities || []).length > 0 && (
              <div data-testid="strategy-variations">
                <p className="text-[9px] font-mono uppercase tracking-[0.15em] text-zinc-400 mb-2">Alternative Strategies</p>
                <div className="grid grid-cols-3 gap-3">
                  {(a.alternative_opportunities || []).slice(0, 3).map((alt, i) => {
                    const altScore = alt.confidence_score >= 70 ? '#22C55E' : alt.confidence_score >= 40 ? '#F59E0B' : '#EF4444';
                    const isActive = activeAlt === i;
                    return (
                      <div key={i} data-testid={`alt-${i}`}
                        onClick={() => { setActiveAlt(p => p === i ? -1 : i); setGenContent({}); setLiveTitle(null); setLiveHeadline(null); setLiveScore(null); }}
                        className={`rounded-xl p-3 cursor-pointer transition-all hover:-translate-y-1 ${isActive ? '' : 'hover:border-[rgba(124,92,255,0.15)]'}`}
                        style={{ background: isActive ? 'rgba(124,92,255,0.06)' : '#0F0F14', border: `1px solid ${isActive ? 'rgba(124,92,255,0.2)' : 'rgba(255,255,255,0.04)'}`, boxShadow: isActive ? '0 0 20px rgba(124,92,255,0.08)' : 'none' }}>
                        <div className="flex items-center justify-between mb-1.5">
                          <span className="text-[10px] font-mono uppercase text-zinc-400">{alt.channel || 'Alt'}</span>
                          <span className="text-[11px] font-mono font-bold" style={{ color: altScore }}>{alt.confidence_score}</span>
                        </div>
                        <p className="text-[12px] text-zinc-200 font-medium leading-snug line-clamp-2 mb-1">{alt.title}</p>
                        <p className="text-[10px] text-zinc-400 line-clamp-1">{alt.audience || alt.hook || ''}</p>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* ═══ NETWORK INTELLIGENCE ═══ */}
            {collective?.intelligence && (
              <div className="space-y-3" data-testid="network-intelligence">
                {/* What Worked for Others Like You */}
                {collective.intelligence.similar_wins?.length > 0 && (
                  <div>
                    <p className="text-[9px] font-mono uppercase tracking-[0.15em] text-zinc-400 mb-2">Founders like you used</p>
                    <div className="space-y-1.5">
                      {collective.intelligence.similar_wins.slice(0, 3).map((w, i) => (
                        <div key={i} className="flex items-center gap-2 rounded-xl px-3 py-2" data-testid={`similar-win-${i}`}
                          style={{ background: '#0F0F14', border: '1px solid rgba(255,255,255,0.04)' }}>
                          <CheckCircle className="w-3 h-3 text-[#22C55E] flex-shrink-0" />
                          <span className="text-[11px] text-zinc-300 flex-1 truncate">{w.strategy} via {w.platform}</span>
                          <span className="text-[10px] font-mono font-bold text-[#22C55E] flex-shrink-0">{w.outcome}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Strategy Leaderboard */}
                {collective.intelligence.leaderboard?.length > 0 && (
                  <div>
                    <p className="text-[9px] font-mono uppercase tracking-[0.15em] text-zinc-400 mb-2">Top Strategies Today</p>
                    <div className="rounded-xl overflow-hidden" style={{ background: '#0F0F14', border: '1px solid rgba(255,255,255,0.04)' }}>
                      {collective.intelligence.leaderboard.slice(0, 5).map((l, i) => {
                        const rankColors = ['#FFD700', '#C0C0C0', '#CD7F32', '#7C5CFF', '#52525B'];
                        return (
                          <div key={i} className="flex items-center gap-2.5 px-3 py-1.5" data-testid={`leaderboard-${i}`}
                            style={{ borderBottom: i < 4 ? '1px solid rgba(255,255,255,0.03)' : 'none' }}>
                            <span className="text-[10px] font-mono font-bold w-5" style={{ color: rankColors[i] || '#52525B' }}>#{l.rank}</span>
                            <span className="text-[11px] text-zinc-200 flex-1 truncate">{l.strategy}</span>
                            <span className="text-[9px] font-mono text-zinc-400">{l.industry}</span>
                            <span className="text-[10px] font-mono font-bold" style={{ color: (l.delta || '').startsWith('+') ? '#22C55E' : '#EF4444' }}>{l.delta}</span>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        {/* ═══ RIGHT — Live Control Panel ═══ */}
        <div className={`${mobileTab === 'controls' ? 'flex' : 'hidden'} md:flex w-full md:w-[20%] border-l p-4 flex-col flex-shrink-0`} style={{ borderColor: 'rgba(255,255,255,0.06)', zIndex: 10 }}>
          <h3 className="text-[10px] font-mono uppercase tracking-[0.15em] text-[#7C5CFF] mb-2">Live Controls</h3>

          {/* Smart Defaults indicator */}
          {collective?.intelligence?.smart_defaults && modKeys.length === 0 && (
            <div className="rounded-lg px-2.5 py-1.5 mb-2" data-testid="smart-defaults"
              style={{ background: 'rgba(124,92,255,0.04)', border: '1px solid rgba(124,92,255,0.1)' }}>
              <p className="text-[8px] font-mono uppercase text-[#7C5CFF]">Network-optimized</p>
              <p className="text-[9px] text-zinc-400">{collective.intelligence.smart_defaults.reason?.slice(0, 60)}</p>
            </div>
          )}

          {/* Impact Preview */}
          {modKeys.length > 0 && (
            <div className="rounded-xl px-3 py-2.5 mb-3 space-y-1.5" style={{ background: '#0F0F14', border: '1px solid rgba(245,158,11,0.15)', boxShadow: '0 0 15px rgba(245,158,11,0.05)' }} data-testid="live-feedback">
              <p className="text-[9px] font-mono uppercase tracking-wider text-[#F59E0B]">Impact Preview</p>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <span className="text-lg font-mono font-bold text-white">{Math.max(0, score + delta)}</span>
                  <span className="text-[11px] font-mono text-[#F59E0B] flex items-center gap-0.5"><ArrowDown className="w-3 h-3" />{Math.abs(delta)}</span>
                </div>
              </div>
              <p className="text-[10px] text-[#F59E0B]">{reason}</p>
              {/* Mode shift indicator */}
              {Object.entries(modeScores).slice(0, 3).map(([mode, sc]) => {
                const m = MODE_MAP[mode]; if (!m) return null;
                const shifted = modKeys.length > 0;
                return (
                  <div key={mode} className="flex items-center gap-1">
                    <span className="text-[8px] font-mono w-12 capitalize" style={{ color: '#52525B' }}>{mode}</span>
                    <div className="flex-1 h-1 rounded-full" style={{ background: '#15151D' }}>
                      <div className="h-full rounded-full transition-all duration-500" style={{ width: `${Math.max(0, sc + (shifted && mode === bestMode ? delta : 0))}%`, background: m.color }} />
                    </div>
                  </div>
                );
              })}
              {regen && <p className="text-[9px] text-[#7C5CFF] flex items-center gap-1"><RefreshCw className="w-3 h-3 animate-spin" />Recalculating...</p>}
            </div>
          )}

          {/* Dropdowns */}
          <div className="space-y-2 flex-1 overflow-y-auto" style={{ scrollbarWidth: 'thin', scrollbarColor: '#1F1F28 transparent', pointerEvents: 'auto' }}>
            {BLOCKS.map(({ key, label }) => {
              const val = bv(key);
              const opts = bo(key);
              const mod = blocks[key]?.selected_value && origBlocks[key]?.selected_value && blocks[key].selected_value !== origBlocks[key].selected_value;
              return (
                <div key={key} className="rounded-xl p-2.5 transition-all" style={{ background: '#0F0F14', border: `1px solid ${mod ? 'rgba(245,158,11,0.2)' : 'rgba(255,255,255,0.04)'}`, boxShadow: mod ? '0 0 8px rgba(245,158,11,0.04)' : 'none' }} data-testid={`ctrl-${key}`}>
                  <p className={`text-[9px] font-mono uppercase tracking-wider mb-1 ${mod ? 'text-[#F59E0B]' : 'text-zinc-400'}`}>{label}{mod ? ' *' : ''}</p>
                  <Select value={val} onValueChange={(v) => onChange(key, v)}>
                    <SelectTrigger className="w-full bg-[#07070A] text-zinc-100 text-sm h-8 rounded-lg border-[rgba(255,255,255,0.06)] hover:border-[#7C5CFF]/40 focus:ring-[#7C5CFF]/30 cursor-pointer" style={{ pointerEvents: 'auto' }} data-testid={`select-${key}`}>
                      <SelectValue placeholder="Select..." />
                    </SelectTrigger>
                    <SelectContent className="bg-[#0F0F14] border-[rgba(255,255,255,0.08)] rounded-xl z-[100]" style={{ zIndex: 100 }}>
                      {opts.map(o => <SelectItem key={o} value={o} className="text-zinc-200 text-sm focus:bg-[#7C5CFF]/10 focus:text-white cursor-pointer rounded-lg">{o}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              );
            })}
          </div>

          {/* Last Deploy + Shortcuts */}
          <div className="pt-3 mt-2 flex-shrink-0 space-y-1.5" style={{ borderTop: '1px solid rgba(255,255,255,0.06)' }}>
            {lastDeploy && (
              <div className="rounded-lg px-2.5 py-2" style={{ background: '#0F0F14', border: '1px solid rgba(255,255,255,0.04)' }} data-testid="last-deployment">
                <div className="flex items-center gap-1.5">
                  <span className={`w-1.5 h-1.5 rounded-full ${lastDeploy.status === 'success' ? 'bg-[#22C55E]' : 'bg-[#EF4444]'}`} />
                  <span className="text-[10px] text-zinc-400 capitalize">{lastDeploy.platform?.replace('_', ' ')}</span>
                  <div className="flex-1" />
                  <span className={`text-[9px] font-mono ${lastDeploy.status === 'success' ? 'text-[#22C55E]' : 'text-[#EF4444]'}`}>{lastDeploy.status === 'success' ? 'Live' : 'Failed'}</span>
                </div>
              </div>
            )}
            {/* Network Position */}
            {collective?.stats?.total_products > 1 && (
              <div className="rounded-lg px-2.5 py-1.5 text-center" data-testid="network-position"
                style={{ background: 'rgba(124,92,255,0.04)', border: '1px solid rgba(124,92,255,0.1)' }}>
                <p className="text-[9px] font-mono text-[#7C5CFF]">Top {Math.max(10, 100 - Math.round((score / 100) * 80))}% in network</p>
                <p className="text-[8px] text-zinc-400 font-mono">{collective.stats.total_products} products analyzed</p>
              </div>
            )}
            <p className="text-[8px] text-zinc-400 font-mono text-center">esc back</p>
          </div>
        </div>
      </div>

      {/* Growth Agent Modal */}
      <GrowthAgentModal
        isOpen={showAgent}
        onClose={() => { setShowAgent(false); fetchProject(); }}
        projectId={id}
        platform={platform}
        content={content}
        opportunity={opp}
        variation={variation}
        score={score}
        goal={modeInfo?.label || bestMode}
      />

      {/* ═══ STRATEGY REPLAY MODAL ═══ */}
      {showReplay && (
        <StrategyReplay
          modeScores={modeScores}
          bestMode={bestMode}
          modeInfo={modeInfo}
          score={score}
          title={title}
          opp={opp}
          fr={fr}
          onClose={() => setShowReplay(false)}
        />
      )}
    </div>
  );
}

// Strategy Replay — animated walkthrough of how AI arrived at recommendation
function StrategyReplay({ modeScores, bestMode, modeInfo, score, title, opp, fr, onClose }) {
  const [step, setStep] = useState(0);
  const [barWidths, setBarWidths] = useState({});
  const [scoreAnim, setScoreAnim] = useState(0);

  // Auto-advance steps
  useEffect(() => {
    if (step >= 4) return;
    const t = setTimeout(() => setStep(s => s + 1), step === 0 ? 800 : 1200);
    return () => clearTimeout(t);
  }, [step]);

  // Animate bars at step 1
  useEffect(() => {
    if (step >= 1) {
      const entries = Object.entries(modeScores);
      entries.forEach(([mode, sc], i) => {
        setTimeout(() => setBarWidths(p => ({ ...p, [mode]: sc })), i * 200);
      });
    }
  }, [step, modeScores]);

  // Animate score at step 3
  useEffect(() => {
    if (step >= 3) {
      let start = 0; const end = score; const dur = 600; const t0 = Date.now();
      const tick = () => { const p = Math.min((Date.now() - t0) / dur, 1); setScoreAnim(Math.round(start + (end - start) * p)); if (p < 1) requestAnimationFrame(tick); };
      tick();
    }
  }, [step, score]);

  const MIcon = modeInfo.icon;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center" data-testid="strategy-replay-modal">
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={onClose} />
      <div className="relative w-full max-w-md mx-4 rounded-2xl overflow-hidden" style={{ background: '#0E0E14', border: '1px solid rgba(124,92,255,0.15)', boxShadow: '0 0 80px rgba(124,92,255,0.1)' }}>
        <div className="absolute top-0 left-0 right-0 h-[2px]" style={{ background: 'linear-gradient(90deg, #7C5CFF, #FF8A3D, #7C5CFF)', opacity: 0.7 }} />

        <div className="flex items-center justify-between px-5 py-3 border-b" style={{ borderColor: 'rgba(255,255,255,0.06)' }}>
          <div className="flex items-center gap-2">
            <Eye className="w-4 h-4 text-[#7C5CFF]" />
            <h2 className="text-sm font-semibold text-white">Strategy Replay</h2>
          </div>
          <button onClick={onClose} data-testid="close-replay" className="text-zinc-400 hover:text-white p-1 rounded-lg hover:bg-[#1F1F28]"><X className="w-4 h-4" /></button>
        </div>

        <div className="px-5 py-5 space-y-5">
          {/* Step 1: Product scan */}
          <div className={`transition-all duration-500 ${step >= 0 ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-2'}`}>
            <div className="flex items-center gap-2 mb-1">
              <div className={`w-5 h-5 rounded-full flex items-center justify-center ${step > 0 ? 'bg-[#22C55E]' : 'bg-[#3B82F6]'}`}>
                {step > 0 ? <Check className="w-3 h-3 text-white" /> : <Loader2 className="w-3 h-3 text-white animate-spin" />}
              </div>
              <span className="text-[11px] font-medium text-white">Scanning product signals</span>
            </div>
            {step > 0 && <p className="ml-7 text-[10px] text-zinc-400 font-mono">"{s(fr.product_truth || 'Product analyzed')}"</p>}
          </div>

          {/* Step 2: Mode scoring */}
          <div className={`transition-all duration-500 ${step >= 1 ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-2'}`}>
            <div className="flex items-center gap-2 mb-2">
              <div className={`w-5 h-5 rounded-full flex items-center justify-center ${step > 1 ? 'bg-[#22C55E]' : step === 1 ? 'bg-[#3B82F6]' : 'bg-[#15151D]'}`}>
                {step > 1 ? <Check className="w-3 h-3 text-white" /> : step === 1 ? <Loader2 className="w-3 h-3 text-white animate-spin" /> : <div className="w-1.5 h-1.5 rounded-full bg-zinc-600" />}
              </div>
              <span className="text-[11px] font-medium text-white">Evaluating strategy modes</span>
            </div>
            {step >= 1 && (
              <div className="ml-7 space-y-1.5">
                {Object.entries(modeScores).map(([mode, sc]) => {
                  const m = MODE_MAP[mode]; if (!m) return null;
                  return (
                    <div key={mode} className="flex items-center gap-2">
                      <span className="text-[9px] font-mono w-16 capitalize text-zinc-400">{mode}</span>
                      <div className="flex-1 h-1.5 rounded-full" style={{ background: '#15151D' }}>
                        <div className="h-full rounded-full transition-all duration-700" style={{ width: `${barWidths[mode] || 0}%`, background: mode === bestMode ? m.color : '#2A2A35' }} />
                      </div>
                      <span className="text-[10px] font-mono w-6 text-right" style={{ color: mode === bestMode ? m.color : '#52525B' }}>{barWidths[mode] || 0}</span>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Step 3: Channel selection */}
          <div className={`transition-all duration-500 ${step >= 2 ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-2'}`}>
            <div className="flex items-center gap-2 mb-1">
              <div className={`w-5 h-5 rounded-full flex items-center justify-center ${step > 2 ? 'bg-[#22C55E]' : step === 2 ? 'bg-[#3B82F6]' : 'bg-[#15151D]'}`}>
                {step > 2 ? <Check className="w-3 h-3 text-white" /> : step === 2 ? <Loader2 className="w-3 h-3 text-white animate-spin" /> : <div className="w-1.5 h-1.5 rounded-full bg-zinc-600" />}
              </div>
              <span className="text-[11px] font-medium text-white">Selecting optimal channel + audience</span>
            </div>
            {step > 2 && <p className="ml-7 text-[10px] text-zinc-400 font-mono">{s(opp.recommended_channel)} → {s(opp.target_audience)}</p>}
          </div>

          {/* Step 4: Confidence lock */}
          <div className={`transition-all duration-500 ${step >= 3 ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-2'}`}>
            <div className="flex items-center gap-2 mb-2">
              <div className={`w-5 h-5 rounded-full flex items-center justify-center ${step > 3 ? 'bg-[#22C55E]' : step === 3 ? 'bg-[#3B82F6]' : 'bg-[#15151D]'}`}>
                {step > 3 ? <Check className="w-3 h-3 text-white" /> : step === 3 ? <Loader2 className="w-3 h-3 text-white animate-spin" /> : <div className="w-1.5 h-1.5 rounded-full bg-zinc-600" />}
              </div>
              <span className="text-[11px] font-medium text-white">Locking confidence score</span>
            </div>
            {step >= 3 && (
              <div className="ml-7 flex items-center gap-3">
                <div className="w-12 h-12 rounded-xl flex items-center justify-center" style={{ background: `${modeInfo.color}10`, border: `1px solid ${modeInfo.color}20` }}>
                  <span className="font-mono text-lg font-bold" style={{ color: scoreAnim >= 70 ? '#22C55E' : '#F59E0B' }}>{scoreAnim}</span>
                </div>
                <div>
                  <p className="text-[11px] text-zinc-300 font-medium">{modeInfo.label} mode selected</p>
                  <p className="text-[9px] text-zinc-400 font-mono">{s(title).slice(0, 50)}{s(title).length > 50 ? '...' : ''}</p>
                </div>
              </div>
            )}
          </div>

          {/* Step 5: Strategy ready */}
          {step >= 4 && (
            <div className="text-center pt-2 anim-scale-in">
              <MIcon className="w-8 h-8 mx-auto mb-2" style={{ color: modeInfo.color }} />
              <p className="text-sm font-bold text-white">Strategy Ready</p>
              <p className="text-[10px] text-zinc-400 mt-1">Your edge is active — act before it fades</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
